import { useMemo, type ReactNode } from 'react';
import { FiArrowDown, FiArrowUp, FiTrendingUp } from 'react-icons/fi';
import { useFinance, usePreferences } from '../../../contexts';
import styles from './SummaryCards.module.css';

interface CardData {
    label: string;
    value: number;
    icon: ReactNode;
    iconClass: string;
    badge: string;
    badgeClass: string;
    valueClass?: string;
}

function formatPeriodBadge(dataInicio: string, dataFim: string, language: string, allTimeLabel: string): string {
    if (!dataInicio || !dataFim) {
        return allTimeLabel;
    }
    const fmt = (d: string) => {
        const [y, m] = d.split('-');
        const label = new Intl.DateTimeFormat(language, { month: 'short' }).format(new Date(2000, Number(m) - 1, 1));
        return `${label.replace('.', '')}/${y}`;
    };
    const ini = fmt(dataInicio);
    const fim = fmt(dataFim);
    return ini === fim ? ini : `${ini} — ${fim}`;
}

export function SummaryCards() {
    const { dashboardSummary, dateFilter } = useFinance();
    const { formatCurrency, t, prefs } = usePreferences();
    console.log('SummaryCards render', { currency: prefs.currency });
    const isResultadoPositive = dashboardSummary.resultado >= 0;
    const periodBadge = useMemo(
        () => formatPeriodBadge(dateFilter.dataInicio, dateFilter.dataFim, prefs.language, t('chart.todoPeriodo')),
        [dateFilter, prefs.language, t]
    );

    const cards: CardData[] = [
        {
            label: t('chart.receitas'),
            value: dashboardSummary.totalIncome,
            icon: <FiArrowUp size={20} />,
            iconClass: styles.iconIncome,
            badge: periodBadge,
            badgeClass: styles.badgeInfo,
        },
        {
            label: t('chart.despesas'),
            value: dashboardSummary.totalExpenses,
            icon: <FiArrowDown size={20} />,
            iconClass: styles.iconExpense,
            badge: periodBadge,
            badgeClass: styles.badgeInfo,
        },
        {
            label: t('chart.resultado'),
            value: dashboardSummary.resultado,
            icon: <FiTrendingUp size={20} />,
            iconClass: isResultadoPositive ? styles.iconIncome : styles.iconExpense,
            badge: isResultadoPositive ? t('chart.positivo') : t('chart.negativo'),
            badgeClass: isResultadoPositive ? styles.badgePositive : styles.badgeNegative,
            valueClass: isResultadoPositive ? styles.valuePositive : styles.valueNegative,
        },
    ];

    return (
        <div className={styles.container}>
            {cards.map((card) => (
                <div key={card.label} className={styles.card}>
                    <div className={`${styles.iconWrapper} ${card.iconClass}`}>
                        {card.icon}
                    </div>
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <span className={styles.label}>{card.label}</span>
                            <span className={`${styles.badge} ${card.badgeClass}`}>
                                {card.badge}
                            </span>
                        </div>
                        <div className={`${styles.value} ${card.valueClass ?? ''}`}>{formatCurrency(card.value)}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}
